import { apiService } from './api';
import { js } from '@config/env';

// ─── Layers ──────────────────────────────────────────────────────────────────

export interface WizardLayer {
  LayerId: number;
  Name: string;
  Color: string;
  TextColor: boolean;
  IsEnable: boolean;
}

export interface WizardLayerUpdateSecurityGroup {
  SecurityGroupID: number;
  Name: string;
}

export interface WizardLayerUpdate {
  LayerId: number;
  Name: string;
  Color: string;
  TextColor: boolean;
  IsEnable: boolean;
  DeleteSecurityGroups: boolean;
  SecurityGroups?: WizardLayerUpdateSecurityGroup[];
}

// ─── Security Groups ──────────────────────────────────────────────────────────

export interface SecurityGroupLayer {
  LayerId: number;
  Name: string;
  Color: string;
  TextColor: boolean;
}

export interface SecurityGroupPerson {
  PersonID?: number;
  PersonId?: number;
  Name: string;
  PathImage: string;
}

export interface SecurityGroupCardType {
  ConfigValueID: number;
  Name: string;
  Icon: string;
}

export interface ProjectListItem {
  ProjectID: number;
  Name: string;
}

export interface LayerListItem {
  LayerId: number;
  Name: string;
  Color: string;
  TextColor: boolean;
  IsEnable: boolean;
  IsDefault: boolean;
}

export interface PersonListItem {
  PersonID: number;
  Name: string;
  PathImage: string;
  isOnLine: boolean;
}

export interface UserSecurityGroup {
  PersonID: number;
  SecurityGroupID: number;
  Name: string;
  Status: boolean | null;
  GroupId: number;
  IsAdminUserGroup: boolean;
  Checked?: boolean;
  IsInternal?: boolean;
}

export interface AdminUser {
  PersonID: number;
  Name: string;
  IsEnabled: boolean;
  IsAdminProject: boolean;
  External: boolean;
  SecurityGroupID: number;
  Status?: boolean;
  EmailAddress: string;
  EditName: boolean;
  DraftName: string | null;
  EditEmail: boolean;
  DraftEmail: string | null;
  PTOApprover?: boolean;
  HRApprover?: boolean;
  SubmitTimeTeam?: boolean;
  SecurityGroups: UserSecurityGroup[];
  GroupIds: string;
  DefaultGroup?: { GroupID: number; GroupName: string; PersonID: number } | null;
}

export interface SecurityGroupListItem {
  SecurityGroupID: number;
  Name: string;
  GroupName: string;
  GroupId: number;
}

export interface MenuItem {
  SideMenuId: number;
  Name: string;
  Text: string;
  Css: string;
  Url: string;
  Icon: string;
  ConfigValueID: number;
  OrderSideMenu: number;
  ShowSeparator: boolean;
}

export interface BasicFieldValue {
  BasicFieldValueId: number;
  BasicFieldId: number;
  Name: string;
  order: number;
  tempOrder: number;
}

export interface BasicField {
  BasicFieldId: number;
  FieldTypeId: number;
  Name: string;
  Description: string;
  ServiceUrl: string | null;
  TextField: string | null;
  ValueField: string | null;
  IsUsedInLayer: boolean | null;
  CollectionType: string | null;
  IsDeleted: boolean;
  BasicFieldValues: BasicFieldValue[];
}

export interface FieldType {
  FieldTypeId: number;
  Name: string;
  DataType: string | null;
  Description: string;
}

export interface AddBasicFieldRequest {
  Name: string;
  Description: string;
  FieldTypeId: number;
  ServiceUrl: string | null;
  CollectionType: string | null;
  ValueField: string | null;
  TextField: string | null;
  BasicFieldValues: { Name: string; tempOrder: number }[] | null;
}

export interface UpdateBasicFieldRequest {
  BasicFieldId: number;
  Name: string;
  Description: string;
  BasicFieldValues: { BasicFieldValueId?: number; Name: string; tempOrder: number }[] | null;
}

export interface ConfigValueItem {
  ConfigValueID: number;
  ConfigItemID: number;
  Value: string;
  SideMenuText: string;
  Icon: string;
  Selected: boolean;
  SideMenuId: number;
  IsEnabled: boolean;
  ConfigValueIDSelected: number | null;
}

export interface CustomFieldType {
  FieldTypeId: number;
  Name: string;
  DataType: string;
  Description: string;
}

export interface CustomField {
  LayerId: number;
  CustomFieldId: number;
  Description: string;
  IsDeleted: boolean;
  IsStatic: boolean;
  Name: string;
  OrderInLayer: number;
  ServiceUrl: string;
  TextField: string;
  ValueField: string;
  FieldTypeId: number;
  ConversationIsEnable: boolean;
  FieldType: CustomFieldType;
}

export interface ConfigValueLayer {
  LayerId: number;
  Name: string;
  Color: string;
  IsEnable: boolean;
  IsDefault: boolean;
  PriorityInConfigValue: number;
  OrderInConfigValue: number;
  CustomFields: CustomField[];
}

export interface ConfigValueDetail {
  ConfigValueID: number;
  ConfigItemID: number;
  Value: string;
  SideMenuText: string;
  Icon: string;
  Layers: ConfigValueLayer[];
}

export interface SecurityGroupProject {
  Name: string;
}

export interface SecuritySubGroup {
  GroupId: any;
  SecurityGroupID: number;
  SecurityGroupId: number;
  Name: string;
  Description: string;
  IsDeleted: boolean;
  IsAdminGroup: boolean;
  View: boolean;
  Edit: boolean;
  Delete: boolean;
  ItemCreation: boolean;
  Temporary: boolean;
  IsBeta: boolean;
  WhiteListIPAddress: string;
  ConfigValueSecurityGroups: SecurityGroupCardType[];
  PeopleSecurityGroup: SecurityGroupPerson[];
  SecurityGroupLayers: SecurityGroupLayer[];
  SecurityGroupProjects: SecurityGroupProject[];
}

export interface SecurityGroup {
  GroupId: number;
  Name: string;
  IsDefault: boolean;
  IsDeleted: boolean;
  IsOpen: boolean;
  GroupAdminUsers: SecurityGroupPerson[];
  SecurityGroups: SecuritySubGroup[];
}

export interface AddResponse {
  createdAccounts: any[];
  duplicatedAccounts: any[];
  estatus: boolean;
  exceptionMessage: string;
  message: string;
  noCreatedAccounts: any[];
}

// ─── Service ─────────────────────────────────────────────────────────────────

const adminConsoleService = {
  // Basic Fields (Custom Details)
  getBasicFields: (): Promise<BasicField[]> => apiService.get<BasicField[]>('basicfield/'),

  getFieldTypes: (): Promise<FieldType[]> =>
    apiService.get<FieldType[]>('CustomField/GetFieldTypes'),

  addBasicField: (data: AddBasicFieldRequest): Promise<number> =>
    apiService.post<number>('basicField/AddBasicField', data),

  updateBasicField: (data: UpdateBasicFieldRequest): Promise<BasicFieldValue[]> =>
    apiService.post<BasicFieldValue[]>('basicField/UpdateBasicField', data),

  deleteBasicField: (id: number): Promise<boolean> =>
    apiService.get<boolean>(`basicfield/DeleteBasicField?Id=${id}`),

  addBasicFieldsToLayer: (
    basicFields: BasicField[],
    layerId: number,
    configValueId: number,
  ): Promise<void> =>
    apiService.post('customfield/AddBasicFieldsToLayers', {
      BasicFields: basicFields,
      LayerId: layerId,
      ConfigValueId: configValueId,
    }),

  // Custom Branding
  updateCustomBranding: (enable: boolean): Promise<void> =>
    apiService.get(`Parameter/UpdateCustomBranding/?enable=${enable}`),

  updateBrandName: (brandName: string): Promise<void> =>
    apiService.get(`Parameter/UpdateBrandName/?brandName=${encodeURIComponent(brandName)}`),

  uploadBrandImage: async (formData: FormData): Promise<string> =>
  {
    let baseUrl = String(js.settings.apiAuthURL || 'http://localhost/Corgee.Web/');
    if (!baseUrl.endsWith('/')) baseUrl += '/';
    const token = localStorage.getItem('C2_access_token');
    const res = await fetch(`${baseUrl}api/Parameter/UploadBrandImage`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!res.ok) throw new Error('Upload failed');
    return res.json();
  },

  // Beta
  updateBeta: (isBeta: boolean): Promise<void> =>
    apiService.get(`Parameter/UpdateBeta/?isBeta=${isBeta}`),

  // Home Settings Widgets (Link to Custom Details)
  getHomeSettingsWidget: (): Promise<
    { id: number; status: string; details: string; persondetails: string; isChecked: boolean }[]
  > => apiService.get('Conversation/GetHomeSettingsWidget'),

  getPersonTypeWidget: (): Promise<string[]> => apiService.get('Conversation/GetPersonTypeWidget'),

  updateHomeSettingsWidget: (widget: {
    id: number;
    status: string;
    details: string;
    persondetails: string;
    isChecked: boolean;
  }): Promise<{
    id: number;
    status: string;
    details: string;
    persondetails: string;
    isChecked: boolean;
  }> => apiService.post('Conversation/UpdateHomeSettingsWidget', widget),

  // Two-Factor Auth
  getTwoFactorAuth: (
    tenantId: number,
  ): Promise<{
    enableTwoFactorAuth: string;
    host: string;
    iKey: string;
    sKey: string;
    aKey: string;
  }> => apiService.get(`Security/getTwoFactorAuth?tenantId=${tenantId}`),

  saveTwoFactorAuth: (data: {
    TenantId: number;
    EnableTwoFactorAuth: string;
    DUO_host: string;
    DUO_ikey: string;
    DUO_skey: string;
    DUO_akey: string;
  }): Promise<void> => apiService.post('Account/saveTwoFactorAuth', data),

  // Layers
  getLayers: (): Promise<WizardLayer[]> => apiService.get<WizardLayer[]>('layer/getall'),

  updateLayers: (layers: WizardLayerUpdate[]): Promise<void> =>
    apiService.post('layer/WizardUpdateLayers', { Layers: layers }),

  // Security Groups
  getSecurityGroups: (): Promise<SecurityGroup[]> => apiService.get<SecurityGroup[]>('Security'),

  saveGroup: (name: string): Promise<number> =>
    apiService.post<number>('Security/SaveGroup', { Name: name }),

  renameGroup: (groupId: number, name: string): Promise<void> =>
    apiService.put('Group/RenameSecurityGroup', { GroupId: groupId, Name: name }),

  deleteGroup: (groupId: number): Promise<void> =>
    apiService.put(`Security/DeleteGroup?GroupID=${groupId}`, {}),

  saveSecurityGroup: (
    groupId: number,
    payload: Partial<SecuritySubGroup> & { Name: string },
  ): Promise<SecuritySubGroup> =>
    apiService.post<SecuritySubGroup>('Security', {
      GroupId: groupId,
      Name: payload.Name,
      Description: payload.Description ?? '',
      View: payload.View ?? false,
      Edit: payload.Edit ?? false,
      Delete: payload.Delete ?? false,
      ItemCreation: payload.ItemCreation ?? false,
      Temporary: payload.Temporary ?? false,
      IsBeta: payload.IsBeta ?? false,
      IsAdminGroup: payload.IsAdminGroup ?? false,
      WhiteListIPAddress: '*',
      ConfigValueSecurityGroups: payload.ConfigValueSecurityGroups ?? [],
      SecurityGroupLayers: payload.SecurityGroupLayers ?? [],
      SecurityGroupProjects: payload.SecurityGroupProjects ?? [],
      PeopleSecurityGroup: payload.PeopleSecurityGroup ?? [],
    }),

  deleteSecurityGroup: (securityGroupId: number): Promise<void> =>
    apiService.put(`Security/DeleteSecurityGroup?securityGroupId=${securityGroupId}`, {}),

  updateSecurityGroupName: (securityGroupId: number, name: string): Promise<void> =>
    apiService.put('Security/updateSecurityGroupDescription', {
      SecurityGroupId: securityGroupId,
      Name: name,
    }),

  getMenuItems: (): Promise<MenuItem[]> => apiService.get<MenuItem[]>('menu/'),

  getConfigValueAll: (): Promise<ConfigValueItem[]> =>
    apiService.get<ConfigValueItem[]>('ConfigItem/getall?allData=false'),

  getConfigValueAllData: (): Promise<ConfigValueItem[]> =>
    apiService.get<ConfigValueItem[]>('ConfigItem/getall?allData=true'),

  addConfigItem: (name: string, icon: string): Promise<ConfigValueItem> =>
    apiService.post<ConfigValueItem>(
      `ConfigItem/add?name=${encodeURIComponent(name)}&icon=${encodeURIComponent(icon)}`,
      {},
    ),

  updateCardTypesValues: (cardTypes: ConfigValueItem[]): Promise<boolean> =>
    apiService.post<boolean>('ConfigItem/UpdateCardTypesValues', { CardTypes: cardTypes }),

  updateCardTypes: (cardTypes: ConfigValueItem[]): Promise<ConfigValueItem[]> =>
    apiService.post<ConfigValueItem[]>('ConfigItem/updatecardtypes', { cardTypes }),

  getConfigValueById: (configValueId: number): Promise<ConfigValueDetail> =>
    apiService.get<ConfigValueDetail>(`configvalue/GetById?configValueId=${configValueId}`),

  sortFieldsInLayer: (layer: ConfigValueLayer): Promise<void> =>
    apiService.post('customfield/SortFieldsInLayer', layer),

  sortLayers: (
    items: { ConfigValueID: number; LayerId: number; PriorityInConfigValue: number }[],
  ): Promise<void> => apiService.post('customfield/SortLayers', items),

  updateSideMenuIcon: (configValueId: number, icon: string): Promise<void> =>
    apiService.post(`Menu/UpdateSideMenuIcon?ConfigValueId=${configValueId}&Icon=${icon}`, {}),

  getPeopleList: (): Promise<PersonListItem[]> =>
    apiService.get<PersonListItem[]>('Person/GetPeopleList/'),

  getAdminUsers: (): Promise<AdminUser[]> =>
    apiService.get<AdminUser[]>('security/getpeoplegroups'),

  getSecurityGroupList: (): Promise<SecurityGroupListItem[]> =>
    apiService.get<SecurityGroupListItem[]>('security/GetSecurityGroupList'),

  updatePersonName: (personId: number, name: string): Promise<void> =>
    apiService.put('Person/UpdateName', { PersonID: personId, Name: name }),

  updatePersonSecurityGroups: (user: AdminUser): Promise<void> =>
    apiService.post('Security/UpdatePersonGroups', user),

  saveUsersGroup: (groupId: number, personIds: number[]): Promise<void> =>
    apiService.post(`Security/SaveUsersGroup?GroupId=${groupId}&Ids=${personIds.join(',')}`, {}),

  updatePersonsSecurityGroups: (SecuritySubGroup: SecuritySubGroup): Promise<void> =>
    apiService.put('security/PersonsSecurityGroup', SecuritySubGroup),

  updateLayersSecurityGroup: (SecuritySubGroup: SecuritySubGroup) =>
    apiService.put('Security/updateLayersSecurityGroup', SecuritySubGroup),

  updateCardsSecurityGroup: (SecuritySubGroup: SecuritySubGroup) =>
    apiService.put('Security/CardsSecurityGroup', SecuritySubGroup),

  updateProjectSecurityGroup: (SecuritySubGroup: SecuritySubGroup) =>
    apiService.put('Security/updateProjectSecurityGroup', SecuritySubGroup),

  deletePerson: (personId: number): Promise<void> =>
    apiService.put(`Person/DeletePerson?PersonID=${personId}`, {}),

  toggleUserStatus: (personId: number, restore: boolean): Promise<void> =>
    apiService.put(
      `Security/DeleteUserGroup?personID=${personId}${restore ? '&restore=true' : ''}`,
      {},
    ),

  resendInvitation: (personId: number): Promise<void> =>
    apiService.post(`Person/ResendInvitation?personId=${personId}`, {}),

  getAllProjects: (): Promise<ProjectListItem[]> =>
    apiService.get<ProjectListItem[]>('project/GetAllProjects/'),

  getLayersList: (): Promise<LayerListItem[]> => apiService.get<LayerListItem[]>('layer/get/'),

  updateWhiteListIPAddress: (securityGroupId: number, whiteListIPAddress: string): Promise<void> =>
    apiService.post(
      `Security/UpdateWhiteList?securityGroupId=${securityGroupId}&whiteList=${encodeURIComponent(whiteListIPAddress)}`,
      {},
    ),

  updatePTOApprover: (personId: number, value: boolean): Promise<boolean> =>
    apiService.get<boolean>(`Security/UpdatePTOApprover?personID=${personId}&PTOApprover=${value}`),

  updateHRApprover: (personId: number, value: boolean): Promise<boolean> =>
    apiService.get<boolean>(`Security/UpdateHRApprover?personID=${personId}&HRApprover=${value}`),

  updateSubmitTimeTeam: (personId: number, value: boolean): Promise<boolean> =>
    apiService.get<boolean>(
      `Security/UpdateSubmitTimeTeam?personID=${personId}&SubmitTimeTeam=${value}`,
    ),

  // Phases Manager
  getPhases: (): Promise<
    { PhaseId: number; Name: string; isEnable: boolean; Disabled: boolean }[]
  > => apiService.get('Phase/GetPhases'),

  createPhase: (phaseId: number, title: string): Promise<void> =>
    apiService.post('Phase/CreateNewOptionPhase', { PhaseId: phaseId, Title: title }),

  editPhase: (phaseId: number, title: string): Promise<void> =>
    apiService.post('Phase/EditOptionPhase', { PhaseId: phaseId, Title: title }),

  deletePhase: (phaseId: number, title: string): Promise<void> =>
    apiService.post('Phase/DeleteOptionPhase', { PhaseId: phaseId, Title: title }),

  reactivatePhase: (phaseId: number, title: string): Promise<void> =>
    apiService.post('Phase/CreateNewOptionPhase', { PhaseId: phaseId, Title: title }),

  // Submit Time Configuration
  getSubmitTimeConfigurations: (): Promise<
    {
      Id: number;
      ConfigValueID: number;
      ItemType: string;
      LayerId: number;
      LayerName: string;
      CustomFieldId: number;
      CustomField: string;
      Required: boolean;
      Editable: boolean;
    }[]
  > => apiService.get('ConfigValue/GetSubmitTimeConfigurations'),

  addSubmitTimeConfiguration: (data: {
    ConfigValueID: number;
    ItemType: string;
    LayerId: number;
    LayerName: string;
    CustomFieldId: number;
    CustomField: string;
    Required: boolean;
    Editable: boolean;
  }): Promise<number> => apiService.post<number>('ConfigValue/AddSubmitTimeConfiguration', data),

  deleteSubmitTimeConfiguration: (configurationId: number): Promise<number> =>
    apiService.put<number>(
      `ConfigValue/DeleteSubmitTimeConfiguration?configurationId=${configurationId}`,
      {},
    ),

  // Company Configurations
  getCompanyConfig: (): Promise<{ WorkingHours: string; WorkingDays: string }> =>
    apiService.get('Account/GetCompanyConfigurationParameters'),

  updateCompanyConfig: (data: { WorkingHours: string; Workingdays: string }): Promise<number> =>
    apiService.post<number>('Account/UpdateCompanyConfigurationParameters', data),

  getHolidays: (): Promise<{ HolidayId: number; Date: string; Description: string | null }[]> =>
    apiService.get('VacationsRequest/GetHolidays'),

  addHoliday: (date: string): Promise<number> =>
    apiService.post<number>('VacationsRequest/AddHoliday', JSON.stringify(date)),

  deleteHoliday: (holidayId: number): Promise<number> =>
    apiService.post<number>('VacationsRequest/DeleteHoliday', holidayId),

  // Positions
  getPositions: (): Promise<
    {
      Id: number;
      Name: string;
      Description: string;
      Rate: number;
    }[]
  > => apiService.get('Positions/GetPositions'),

  updatePosition: (payload: {
    PositionId: number | null;
    Name: string;
    Description: string;
    Rate: string;
    Action: 'add' | 'edit' | 'delete';
  }): Promise<boolean> => apiService.post<boolean>('Positions/UpdatePositions', payload),

  // Subscription / License
  getLicense: (): Promise<{
    CustomerName: string;
    CustomerEmail: string;
    TotalUsers: number;
    LicenseType: string;
    DateExpiration: string;
    Expired: boolean;
    ErrorExpiredMessage: string | null;
    Signatured: boolean;
    ErrorSignaturedMessage: string | null;
  }> =>
  {
    let baseUrl = String(js.settings.apiAuthURL || 'http://localhost/Corgee.Web/');
    if (!baseUrl.endsWith('/')) baseUrl += '/';
    const token = localStorage.getItem('C2_access_token');
    return fetch(`${baseUrl}License/GetLicense`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then(r =>
    {
      if (!r.ok) throw new Error('Failed to load license');
      return r.json();
    });
  },

  uploadLicense: async (licFile: File, pkeyFile: File): Promise<void> =>
  {
    let baseUrl = String(js.settings.apiAuthURL || 'http://localhost/Corgee.Web/');
    if (!baseUrl.endsWith('/')) baseUrl += '/';
    const token = localStorage.getItem('C2_access_token');
    const formData = new FormData();
    formData.append('file', licFile, licFile.name);
    formData.append('file', pkeyFile, pkeyFile.name);
    formData.append('filename', licFile.name);
    const res = await fetch(`${baseUrl}License/UploadLicense`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!res.ok) throw new Error('Upload failed');
  },

  updateSecurityGroupPermissions: (
    securityGroupID: number,
    perms: {
      View: boolean;
      Edit: boolean;
      Delete: boolean;
      ItemCreation: boolean;
      Temporary: boolean;
      IsBeta: boolean;
    },
  ): Promise<void> =>
    apiService.put('Security/updatePermissionSecurityGroup', { securityGroupID, ...perms }),

  // Invite Users
  inviteUsers: (request: any): Promise<any> => apiService.post<any>('Account/AddPeople', request),

  // Report Configuration
  getReportConfigurations: (): Promise<
    {
      Id: number;
      ReportTypeId: number;
      ReportTypeName: string;
      ConfigValueID: number;
      ItemType: string;
      LayerId: number;
      LayerName: string;
      CustomFieldId: number;
      CustomField: string;
    }[]
  > => apiService.get('ConfigValue/GetReportConfigurations'),

  updateReportConfiguration: (data: {
    Id: number | null;
    ReportTypeId: number;
    ConfigValueID: number;
    ItemType: string;
    LayerId: number;
    LayerName: string;
    CustomFieldId: number;
    CustomField: string;
    ReportTypeName?: string;
  }): Promise<number> => apiService.post<number>('ConfigValue/UpdateReportConfiguration', data),

  addReportConfiguration: (data: {
    ReportTypeId: number;
    ConfigValueID: number;
    ItemType: string;
    LayerId: number;
    LayerName: string;
    CustomFieldId: number;
    CustomField: string;
    ReportTypeName?: string;
  }): Promise<number> => apiService.post<number>('ConfigValue/AddReportConfiguration', data),

  deleteReportConfiguration: (id: number): Promise<number> =>
    apiService.put<number>(`ConfigValue/DeleteReportConfiguration?configurationId=${id}`, {}),

  getBasicFieldsSP: (configValueId: number, layerId: number): Promise<BasicField[]> =>
    apiService.get(`basicfield/GetBasicFields?ConfigValueId=${configValueId}&LayerId=${layerId}`),

  addBasicFieldsToLayers: (request: {
    BasicFields: BasicField[];
    ConfigValueId: number;
    LayerId: number;
  }): Promise<CustomField[]> => apiService.post(`customfield/AddBasicFieldsToLayers`, request),

  deleteLayerField: (request: CustomField) =>
    apiService.post(`customfield/DeleteLayerField`, request),

  getbyconfigitem: (request: number) =>
    apiService.get(`ConfigItem/getbyconfigitem?configItemId=${request}`),

  updateCardTypeSelected: (request: any) =>
    apiService.post(`ConfigValue/updateCardTypeSelected`, request),
};

export default adminConsoleService;
